import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Avatar, Box, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import LocalMallRoundedIcon from '@mui/icons-material/LocalMallRounded';
import KeyboardReturnRoundedIcon from '@mui/icons-material/KeyboardReturnRounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';

import { daysInMonthForLabel, fmtInt, fmtMoney, getRollingDashboardLabelForDate, getRollingDashboardLabels, periodCopy } from './DashboardHelpers';
import { useDashboardColors } from './DashboardColors';
import { DashboardCard, DashboardGrid, DashboardIconBadge } from './DashboardLayout';
import { DashboardChartCard } from './DashboardGraph';
import { DashboardKpi, DashboardKpiOverviewCard } from './DashboardKpiOverview';
import type { ChartDataProvider, ChartRequest, DashboardBarKey } from 'src/pages/dashboard/DashboardGraph';
import type { DashboardCreditNotesRow, DashboardDispatchedRow, DashboardPurchasingRow, DashboardReturnsRow, DashboardSalesRow, DashboardToShipRow } from 'src/pages/dashboard/DashboardProviders';
import type { Period, SalesChannel, SalesRep } from 'src/pages/dashboard/DashboardFilters';

/** @deprecated sizes are now handled by DashboardGrid (CSS grid areas); kept so old call sites still compile. */
type CardSizeProps = { xs?: number; sm?: number; md?: number; lg?: number; xl?: number };

type DashboardSalesMarginsRow = {
  SalesDate?: string | Date | null;
  OrderChannel?: string | null;
  ContributionMargin?: number | null;
  ContributionMarginRatio?: number | null;
};

export type DashboardCardMetrics = {
  range: { currentLabel: string; windowLabels: string[] };
  orders: { current: { online: number; store: number; total: number }; window: { online: number; store: number; total: number }; avgPerDay: { online: number; store: number; total: number }; previousWindowTotal?: number | null };
  orderValue: { current: { online: number; store: number; total: number }; window: { online: number; store: number; total: number }; avgPerDay: { online: number; store: number; total: number }; previousWindowTotal?: number | null };
  salesMargins: { hasData: boolean; current: { online: number; store: number; total: number }; window: { online: number; store: number; total: number }; avgPerDay: { online: number; store: number; total: number }; marginRatio: { online: number | null; store: number | null; total: number | null } };
  returns: { current: { online: number; store: number; total: number }; window: { online: number; store: number; total: number }; avgPerDay: { online: number; store: number; total: number }; previousWindowTotal?: number | null; rate: number | null; creditRate: number | null; trendPct: number | null };
  purchasing: { value: number };
  shipping: { shipped: number; toShip: number };
  creditNotes: { qty: number; value: number };
};

export type DashboardCardsProps = {
  chartPeriod: Period;
  selectedBars: DashboardBarKey[];
  selectedSalesChannels: SalesChannel[];
  viewBySalesRep: boolean;
  selectedSalesReps: string[];
  salesRepsData: SalesRep[];
  dataProvider: ChartDataProvider;
  dashboardSales?: DashboardSalesRow[];
  dashboardReturns?: DashboardReturnsRow[];
  dashboardPurchasing: DashboardPurchasingRow[];
  dashboardDispatched: DashboardDispatchedRow[];
  dashboardToShip: DashboardToShipRow[];
  dashboardCreditNotes: DashboardCreditNotesRow[];
  dashboardSalesMargins?: DashboardSalesMarginsRow[];
  kpis?: DashboardKpi[];
};

/** Month window = 4 × 7 days (see DashboardHelpers.getRollingDashboardLabels). */
const MONTH_WINDOW_DAYS = 28;

const sumByLabels = (labelsPick: string[], allLabels: string[], series: number[]) => {
  const indexByLabel = new Map<string, number>();
  allLabels.forEach((label, index) => indexByLabel.set(label, index));
  return labelsPick.reduce((acc, label) => acc + (series[indexByLabel.get(label) ?? -1] ?? 0), 0);
};

const pack = (online: number, store: number) => ({ online, store, total: online + store });
const pctChange = (current: number, previous: number) => {
  if (!Number.isFinite(previous) || previous < 0) return null;
  if (previous === 0) return current === 0 ? 0 : 100;
  return ((current - previous) / previous) * 100;
};
const fmtPct = (value: number | null, digits = 1) => (value == null || !Number.isFinite(value) ? '—' : `${value.toFixed(digits)}%`);
const ratioFromParts = (margin: number, revenue: number) => revenue > 0 ? (margin / revenue) * 100 : null;

export const inferSalesChannelFromOrderChannel = (orderChannel?: string | null): 'Store' | 'Online' | null => {
  const normalized = String(orderChannel ?? '').trim().toLowerCase();
  if (normalized === 'web' || normalized === 'online' || normalized === 'direct' ) return 'Online';
  if (normalized === 'store') return 'Store';
  return null;
};

type MetricsArgs = {
  dataProvider: ChartDataProvider;
  period: Period;
  dashboardSales?: DashboardSalesRow[];
  dashboardReturns?: DashboardReturnsRow[];
  dashboardPurchasing?: DashboardPurchasingRow[];
  dashboardDispatched?: DashboardDispatchedRow[];
  dashboardToShip?: DashboardToShipRow[];
  dashboardCreditNotes?: DashboardCreditNotesRow[];
  dashboardSalesMargins?: DashboardSalesMarginsRow[];
};

export const buildDashboardCardMetrics = (args: MetricsArgs): DashboardCardMetrics => {
  const { dataProvider, period, dashboardSalesMargins = [] } = args;
  const salesRows = args.dashboardSales ?? [];
  const returnsRows = args.dashboardReturns ?? [];

  const labels = getRollingDashboardLabels(period);
  const currentLabel = labels[labels.length - 1] ?? '';
  const windowLabels = labels;

  // Previous window has the same length as the current one (Week 7 days, Month 28 days). Year has no comparison.
  const previousWindowRange = (() => {
    const end = new Date();
    end.setHours(0, 0, 0, 0);
    const start = new Date(end);
    const length = period === 'Week' ? 7 : period === 'Month' ? MONTH_WINDOW_DAYS : 0;
    if (!length) return null;
    end.setDate(end.getDate() - length);
    start.setTime(end.getTime());
    start.setDate(start.getDate() - (length - 1));
    return { start, end };
  })();

  const req: ChartRequest = {
    period,
    selectedBars: ['ordersBar', 'ordersValueBar', 'returnsBar', 'toShipBar', 'shippedBar', 'purchasesValueBar', 'creditNotesBar', 'creditNotesValueBar'],
    selectedChannels: ['Online', 'Store'],
    byRep: false,
    selectedReps: [],
    allReps: [],
    orientation: 'vertical',
  };

  const buckets = dataProvider.getStandardBuckets(req, labels);

  const salesMarginsByLabel = new Map<string, { online: number; store: number; onlineRevenue: number; storeRevenue: number }>();
  labels.forEach(label => salesMarginsByLabel.set(label, { online: 0, store: 0, onlineRevenue: 0, storeRevenue: 0 }));

  dashboardSalesMargins.forEach(row => {
    if (!row.SalesDate) return;
    const label = getRollingDashboardLabelForDate(row.SalesDate, period, labels);
    if (!label) return;
    const channel = inferSalesChannelFromOrderChannel(row.OrderChannel);
    if (!channel) return;
    const contributionMargin = Number(row.ContributionMargin ?? 0) || 0;
    const contributionMarginRatio = Number(row.ContributionMarginRatio ?? 0) || 0;
    const impliedRevenue = contributionMarginRatio !== 0 && Number.isFinite(contributionMarginRatio) ? contributionMargin / contributionMarginRatio : 0;
    const bucket = salesMarginsByLabel.get(label)!;
    if (channel === 'Online') {
      bucket.online += contributionMargin;
      bucket.onlineRevenue += impliedRevenue;
    } else {
      bucket.store += contributionMargin;
      bucket.storeRevenue += impliedRevenue;
    }
  });

  const salesMarginsOnlineSeries = labels.map(label => salesMarginsByLabel.get(label)?.online ?? 0);
  const salesMarginsStoreSeries = labels.map(label => salesMarginsByLabel.get(label)?.store ?? 0);
  const salesMarginRevenueOnlineSeries = labels.map(label => salesMarginsByLabel.get(label)?.onlineRevenue ?? 0);
  const salesMarginRevenueStoreSeries = labels.map(label => salesMarginsByLabel.get(label)?.storeRevenue ?? 0);

  const cur = (series: number[]) => sumByLabels([currentLabel], labels, series);
  const win = (series: number[]) => sumByLabels(windowLabels, labels, series);

  const ordersWindowOnline = win(buckets.ordersOnline);
  const ordersWindowStore = win(buckets.ordersStore);
  const valueWindowOnline = win(buckets.ordersValueOnline);
  const valueWindowStore = win(buckets.ordersValueStore);
  const returnsWindowOnline = win(buckets.returnsOnline);
  const returnsWindowStore = win(buckets.returnsStore);
  const salesMarginsWindowOnline = win(salesMarginsOnlineSeries);
  const salesMarginsWindowStore = win(salesMarginsStoreSeries);

  const inPreviousWindow = (value?: string | Date | null) => {
    if (!previousWindowRange || !value) return false;
    const dt = new Date(value);
    if (!Number.isFinite(+dt)) return false;
    dt.setHours(0, 0, 0, 0);
    return dt >= previousWindowRange.start && dt <= previousWindowRange.end;
  };

  const hasPreviousSalesData = previousWindowRange ? salesRows.some(row => inPreviousWindow(row?.SalesDate)) : false;
  const hasPreviousReturnsData = previousWindowRange ? returnsRows.some(row => inPreviousWindow(row?.ReturnCreateDate)) : false;
  const previousOrdersTotal = salesRows.reduce((sum, row) => sum + (inPreviousWindow(row?.SalesDate) ? (Number(row?.Count ?? 0) || 0) : 0), 0);
  const previousOrderValueTotal = salesRows.reduce((sum, row) => sum + (inPreviousWindow(row?.SalesDate) ? (Number(row?.TotalAmountNet ?? 0) || 0) : 0), 0);
  const previousReturnsTotal = returnsRows.reduce((sum, row) => sum + (inPreviousWindow(row?.ReturnCreateDate) ? (Number(row?.Count ?? 0) || 0) : 0), 0);

  const purchasingValue = win(buckets.purchasesValueOnline) + win(buckets.purchasesValueStore);
  const shipped = win(buckets.shippedOnline) + win(buckets.shippedStore);
  const toShip = win(buckets.toShipOnline) + win(buckets.toShipStore);
  const creditQty = win(buckets.creditNotesOnline) + win(buckets.creditNotesStore);
  const creditValue = win(buckets.creditNotesValueOnline) + win(buckets.creditNotesValueStore);

  let divisorDays = 1;
  if (period === 'Week') divisorDays = 7;
  else if (period === 'Month') divisorDays = MONTH_WINDOW_DAYS;
  else divisorDays = Math.max(1, windowLabels.map(label => daysInMonthForLabel(label)).reduce((a, b) => a + b, 0));

  return {
    range: { currentLabel, windowLabels },
    orders: {
      current: pack(cur(buckets.ordersOnline), cur(buckets.ordersStore)),
      window: pack(ordersWindowOnline, ordersWindowStore),
      avgPerDay: pack(ordersWindowOnline / divisorDays, ordersWindowStore / divisorDays),
      previousWindowTotal: previousWindowRange && hasPreviousSalesData ? previousOrdersTotal : null,
    },
    orderValue: {
      current: pack(cur(buckets.ordersValueOnline), cur(buckets.ordersValueStore)),
      window: pack(valueWindowOnline, valueWindowStore),
      avgPerDay: pack(valueWindowOnline / divisorDays, valueWindowStore / divisorDays),
      previousWindowTotal: previousWindowRange && hasPreviousSalesData ? previousOrderValueTotal : null,
    },
    salesMargins: {
      hasData: dashboardSalesMargins.length > 0,
      current: pack(cur(salesMarginsOnlineSeries), cur(salesMarginsStoreSeries)),
      window: pack(salesMarginsWindowOnline, salesMarginsWindowStore),
      avgPerDay: pack(salesMarginsWindowOnline / divisorDays, salesMarginsWindowStore / divisorDays),
      marginRatio: {
        online: ratioFromParts(salesMarginsWindowOnline, win(salesMarginRevenueOnlineSeries)),
        store: ratioFromParts(salesMarginsWindowStore, win(salesMarginRevenueStoreSeries)),
        total: ratioFromParts(salesMarginsWindowOnline + salesMarginsWindowStore, win(salesMarginRevenueOnlineSeries) + win(salesMarginRevenueStoreSeries)),
      },
    },
    returns: {
      current: pack(cur(buckets.returnsOnline), cur(buckets.returnsStore)),
      window: pack(returnsWindowOnline, returnsWindowStore),
      avgPerDay: pack(returnsWindowOnline / divisorDays, returnsWindowStore / divisorDays),
      previousWindowTotal: previousWindowRange && hasPreviousReturnsData ? previousReturnsTotal : null,
      rate: (ordersWindowOnline + ordersWindowStore) > 0 ? ((returnsWindowOnline + returnsWindowStore) / (ordersWindowOnline + ordersWindowStore)) * 100 : null,
      creditRate: (valueWindowOnline + valueWindowStore) > 0 ? (creditValue / (valueWindowOnline + valueWindowStore)) * 100 : null,
      trendPct: previousWindowRange && hasPreviousReturnsData ? pctChange(returnsWindowOnline + returnsWindowStore, previousReturnsTotal) : null,
    },
    purchasing: { value: purchasingValue },
    shipping: { shipped, toShip },
    creditNotes: { qty: creditQty, value: creditValue },
  };
};

export const useDashboardCardMetrics = (args: MetricsArgs) => {
  const { dataProvider, period, dashboardSales, dashboardReturns, dashboardPurchasing, dashboardDispatched, dashboardToShip, dashboardCreditNotes, dashboardSalesMargins } = args;
  return useMemo(
    () => buildDashboardCardMetrics({ dataProvider, period, dashboardSales, dashboardReturns, dashboardPurchasing, dashboardDispatched, dashboardToShip, dashboardCreditNotes, dashboardSalesMargins }),
    [dataProvider, period, dashboardSales, dashboardReturns, dashboardPurchasing, dashboardDispatched, dashboardToShip, dashboardCreditNotes, dashboardSalesMargins],
  );
};

/* ============================================================================
 * Small shared pieces
 * ==========================================================================*/

/** ▲/▼ change chip. Green/red = good/bad (e.g. more returns is red), so it never relies on colour alone. */
export const DashboardTrendChip = ({ value, upIsGood = true, title }: { value: number | null | undefined; upIsGood?: boolean; title?: string }) => {
  const theme = useTheme();
  if (value == null || !Number.isFinite(value)) return null;
  const flat = Math.abs(value) < 0.5;
  const good = (value > 0) === upIsGood;
  const color = flat ? theme.palette.text.secondary : good ? theme.palette.success.main : theme.palette.error.main;
  return (
    <Box
      component="span"
      title={title}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, px: 0.75, borderRadius: 999, fontSize: 11.5, fontWeight: 700, lineHeight: 1.6, letterSpacing: 0, textTransform: 'none', color, bgcolor: alpha(color, 0.14), whiteSpace: 'nowrap' }}
    >
      {flat ? '±0%' : `${value > 0 ? '▲' : '▼'} ${Math.abs(value).toFixed(0)}%`}
    </Box>
  );
};

const captionSx = { textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, color: 'text.secondary', fontSize: 11.5, lineHeight: 1.4 } as const;

/* ============================================================================
 * PERFORMANCE — the original card, aligned to an icon column + fixed rhythm.
 * 1 column (narrow / 1/3 width) → 2 columns (≥620px card) → one row (≥1050px card)
 * ==========================================================================*/

const PerformanceItem = ({ icon, color, tooltip, title, trend, value, extra, currentLabel, currentValue, avg }: {
  icon: React.ReactNode; color: string; tooltip: string; title: string; trend?: React.ReactNode;
  value: React.ReactNode; extra?: React.ReactNode; currentLabel: string; currentValue: string; avg: string;
}) => {
  const { t } = useTranslation();
  return (
    <Box className="pf-row">
      <Tooltip title={tooltip} arrow placement="top">
        <Box className="pf-badge-wrap"><DashboardIconBadge color={color}>{icon}</DashboardIconBadge></Box>
      </Tooltip>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', minHeight: 20 }}>
          <Typography component="span" sx={captionSx}>{title}</Typography>
          {trend}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', my: 0.25 }}>
          <Typography className="pf-value" component="span" sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1.15, color: 'text.primary', whiteSpace: 'nowrap' }}>{value}</Typography>
          {extra}
        </Box>
        <Box sx={{ display: 'flex', gap: '2px 14px', flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">{currentLabel}: <Typography component="span" variant="caption" fontWeight={650} color="text.primary">{currentValue}</Typography></Typography>
          <Typography variant="caption" color="text.secondary">{t('Avg/day')}: <Typography component="span" variant="caption" fontWeight={650} color="text.primary">{avg}</Typography></Typography>
        </Box>
      </Box>
    </Box>
  );
};

const performanceSx = (count: number): SxProps<Theme> => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  height: '100%',
  alignContent: 'space-between',
  '& .pf-row': { display: 'grid', gridTemplateColumns: '44px minmax(0, 1fr)', columnGap: '14px', alignItems: 'center', py: 1.5, borderTop: 1, borderColor: 'divider' },
  '& .pf-row:first-of-type': { borderTop: 0, pt: 0.5 },
  '& .pf-row:last-of-type': { pb: 0 },
  '& .pf-duo': { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  '@container (min-width: 620px)': {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    columnGap: '28px',
    alignContent: 'start',
    '& .pf-row:nth-of-type(2)': { borderTop: 0, pt: 0.5 },
    ...(count % 2 === 1 ? {
      '& .pf-row:last-of-type': { gridColumn: 'span 2' },
      // line "Credit rate" up with the text column of the right-hand items
      '& .pf-row:last-of-type .pf-duo': { gridTemplateColumns: 'calc((100% + 58px) / 2 + 2px) 1fr' },
    } : {}),
  },
  '@container (min-width: 1050px)': {
    gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`,
    columnGap: 0,
    '& .pf-row': { borderTop: 0, py: 0.25, px: 2.25, borderLeft: 1, borderColor: 'divider', gridTemplateColumns: '1fr', alignItems: 'start', alignContent: 'start' },
    '& .pf-row:first-of-type': { borderLeft: 0, pl: 0 },
    '& .pf-row:last-of-type': { gridColumn: 'auto' },
    '& .pf-row:last-of-type .pf-duo': { gridTemplateColumns: '1fr 1fr' },
    '& .pf-badge-wrap': { mb: 1, width: 36, '& > div': { width: 36, height: 36 } },
    '& .pf-value': { fontSize: 22 },
  },
});

export const DashboardExecutiveSummaryCard: React.FC<{ metrics: DashboardCardMetrics; period: Period; sizes?: CardSizeProps; minHeight?: number }> = ({ metrics, period }) => {
  const { t } = useTranslation();
  const colors = useDashboardColors();
  const copy = periodCopy(period);
  const trendTitle = t('Change vs previous period');

  const revenueTrend = metrics.orderValue.previousWindowTotal != null ? pctChange(metrics.orderValue.window.total, metrics.orderValue.previousWindowTotal) : null;
  const ordersTrend = metrics.orders.previousWindowTotal != null ? pctChange(metrics.orders.window.total, metrics.orders.previousWindowTotal) : null;
  const count = metrics.salesMargins.hasData ? 5 : 4;

  return (
    <DashboardCard area="performance" title={t('Performance ({{windowHeader}})', { windowHeader: copy.windowHeader })}>
      <Box sx={performanceSx(count)}>
        <PerformanceItem
          icon={<PaidRoundedIcon />} color={colors.orderValue.store}
          tooltip={t('Total revenue is all money coming in from orders, including products, tax, shipping and other charges before returns or credits are subtracted.')}
          title={t('Total Revenue')} trend={<DashboardTrendChip value={revenueTrend} title={trendTitle} />}
          value={fmtMoney(metrics.orderValue.window.total)}
          currentLabel={copy.currentLabel} currentValue={fmtMoney(metrics.orderValue.current.total)} avg={fmtMoney(metrics.orderValue.avgPerDay.total)}
        />
        {metrics.salesMargins.hasData && (
          <PerformanceItem
            icon={<PercentRoundedIcon />} color={colors.salesMargins.store}
            tooltip={t('Contribution margin is the money gained from the product markup after product cost. It does not include other costs like ads, salaries, rent or tools that can reduce the final profit.')}
            title={t('Contribution Margin')}
            value={fmtMoney(metrics.salesMargins.window.total)}
            extra={(
              <Tooltip title={t('This percentage is the total average markup for the selected period. It compares total contribution margin with the revenue behind those sales.')} arrow placement="top">
                <Typography component="span" sx={{ fontSize: 14, fontWeight: 700, color: colors.salesMargins.store, cursor: 'help' }}>{fmtPct(metrics.salesMargins.marginRatio.total)}</Typography>
              </Tooltip>
            )}
            currentLabel={copy.currentLabel} currentValue={fmtMoney(metrics.salesMargins.current.total)} avg={fmtMoney(metrics.salesMargins.avgPerDay.total)}
          />
        )}
        <PerformanceItem
          icon={<LocalMallRoundedIcon />} color={colors.orders.store}
          tooltip={t('Total number of orders for the selected period window')}
          title={t('Total Orders')} trend={<DashboardTrendChip value={ordersTrend} title={trendTitle} />}
          value={fmtInt(metrics.orders.window.total)}
          currentLabel={copy.currentLabel} currentValue={fmtInt(metrics.orders.current.total)}
          avg={metrics.orders.avgPerDay.total.toLocaleString('sv-SE', { maximumFractionDigits: 1 })}
        />
        <PerformanceItem
          icon={<KeyboardReturnRoundedIcon />} color={colors.returns}
          tooltip={t('Returns are the number of orders customers sent back during this period.')}
          title={t('Returns')} trend={<DashboardTrendChip value={metrics.returns.trendPct} upIsGood={false} title={trendTitle} />}
          value={fmtInt(metrics.returns.window.total)}
          currentLabel={copy.currentLabel} currentValue={fmtInt(metrics.returns.current.total)}
          avg={metrics.returns.avgPerDay.total.toLocaleString('sv-SE', { maximumFractionDigits: 1 })}
        />
        <Box className="pf-row">
          <Tooltip title={t('Return rate and credit rate show how much returns affect orders and revenue.')} arrow placement="top">
            <Box className="pf-badge-wrap"><DashboardIconBadge color={colors.credits}><PercentRoundedIcon /></DashboardIconBadge></Box>
          </Tooltip>
          <Box className="pf-duo">
            <Tooltip title={t('Return rate is the number of returns as a percentage of the total number of orders.')} arrow placement="top">
              <Box>
                <Typography component="div" sx={captionSx}>{t('Return rate')}</Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.25, color: 'text.primary' }}>{fmtPct(metrics.returns.rate)}</Typography>
              </Box>
            </Tooltip>
            <Tooltip title={t('Credit rate is the money paid back through credits and returns compared with total revenue.')} arrow placement="top">
              <Box>
                <Typography component="div" sx={captionSx}>{t('Credit rate')}</Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.25, color: 'text.primary' }}>{fmtPct(metrics.returns.creditRate)}</Typography>
              </Box>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </DashboardCard>
  );
};

/* ============================================================================
 * CHANNEL BREAKDOWN — split bars, metric hue with Online (light) / Store (dark) shades
 * ==========================================================================*/

const ChannelSplitRow = ({ icon, label, online, store, formatter, onlineColor, storeColor, footer }: {
  icon: React.ReactNode; label: string; online: number; store: number; formatter: (n: number) => string;
  onlineColor: string; storeColor: string; footer?: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const total = online + store;
  const pctOnline = total ? Math.round((online / total) * 100) : 0;
  const pctStore = total ? 100 - pctOnline : 0;

  return (
    <Box sx={{ py: 1.25, borderBottom: 1, borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary', '& svg': { fontSize: 17 } }}>
          {icon}<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{label}</Typography>
        </Box>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'text.primary', whiteSpace: 'nowrap' }}>{formatter(total)}</Typography>
      </Box>
      <Box role="img" aria-label={`${t('Online')} ${pctOnline}%, ${t('Store')} ${pctStore}%`} sx={{ display: 'flex', gap: '2px', height: 10, borderRadius: 5, overflow: 'hidden', bgcolor: 'action.hover' }}>
        {online > 0 && <Box sx={{ flex: online, bgcolor: onlineColor }} />}
        {store > 0 && <Box sx={{ flex: store, bgcolor: storeColor }} />}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '2px 12px', mt: 0.875 }}>
        <Typography variant="caption" color="text.secondary"><Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', bgcolor: onlineColor, mr: 0.75 }} />{t('Online')} <Box component="b" sx={{ color: 'text.primary' }}>{pctOnline}%</Box> · {formatter(online)}</Typography>
        <Typography variant="caption" color="text.secondary"><Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', bgcolor: storeColor, mr: 0.75 }} />{t('Store')} <Box component="b" sx={{ color: 'text.primary' }}>{pctStore}%</Box> · {formatter(store)}</Typography>
      </Box>
      {footer ? <Box sx={{ mt: 0.5 }}>{footer}</Box> : null}
    </Box>
  );
};

export const DashboardChannelBreakdownCard: React.FC<{ metrics: DashboardCardMetrics; period: Period; sizes?: CardSizeProps; minHeight?: number }> = ({ metrics, period }) => {
  const { t } = useTranslation();
  const colors = useDashboardColors();
  const copy = periodCopy(period);
  const fmtQty = (n: number) => `${fmtInt(n)} ${t('st')}`;

  return (
    <DashboardCard area="channel" title={t('Channel Breakdown ({{windowHeader}})', { windowHeader: copy.windowHeader })} bodySx={{ justifyContent: 'space-around' }}>
      <ChannelSplitRow icon={<PaidRoundedIcon />} label={t('Revenue Share')} online={metrics.orderValue.window.online} store={metrics.orderValue.window.store} formatter={fmtMoney} onlineColor={colors.orderValue.online} storeColor={colors.orderValue.store} />
      {metrics.salesMargins.hasData && (
        <ChannelSplitRow
          icon={<PercentRoundedIcon />} label={t('Margin Share')}
          online={metrics.salesMargins.window.online} store={metrics.salesMargins.window.store} formatter={fmtMoney}
          onlineColor={colors.salesMargins.online} storeColor={colors.salesMargins.store}
          footer={(
            <Typography variant="caption" color="text.secondary">
              {t('Online margin')}: <Box component="b" sx={{ color: 'text.primary' }}>{fmtPct(metrics.salesMargins.marginRatio.online)}</Box>
              {'  ·  '}
              {t('Store margin')}: <Box component="b" sx={{ color: 'text.primary' }}>{fmtPct(metrics.salesMargins.marginRatio.store)}</Box>
            </Typography>
          )}
        />
      )}
      <ChannelSplitRow icon={<LocalMallRoundedIcon />} label={t('Orders Share (Qty)')} online={metrics.orders.window.online} store={metrics.orders.window.store} formatter={fmtQty} onlineColor={colors.orders.online} storeColor={colors.orders.store} />
    </DashboardCard>
  );
};

/* ============================================================================
 * OPERATIONS — one strip of cells (2 columns when narrow, one row when wide)
 * ==========================================================================*/

const OperationsCell = ({ icon, color, label, value, tooltip }: { icon: React.ReactNode; color: string; label: string; value: string; tooltip: string }) => (
  <Tooltip title={tooltip} arrow placement="top">
    <Box className="ops-cell" sx={{ bgcolor: 'background.paper', px: 1.75, py: 1.5, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.5, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, '& svg': { fontSize: 17, color } }}>
        {icon}<Typography component="span" sx={captionSx}>{label}</Typography>
      </Box>
      <Typography sx={{ fontSize: 22, fontWeight: 700, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</Typography>
    </Box>
  </Tooltip>
);

export const DashboardOperationsOverviewCard: React.FC<{ metrics: DashboardCardMetrics; period: Period; sizes?: CardSizeProps; minHeight?: number }> = ({ metrics, period }) => {
  const { t } = useTranslation();
  const colors = useDashboardColors();
  const copy = periodCopy(period);

  return (
    <DashboardCard area="operations" title={t('Operations Overview ({{windowHeader}})', { windowHeader: copy.windowHeader })}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gridAutoRows: '1fr',
          gap: '1px',
          bgcolor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          height: '100%',
          '& .ops-cell:last-of-type': { gridColumn: 'span 2' },
          '@container (min-width: 700px)': {
            gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
            '& .ops-cell:last-of-type': { gridColumn: 'auto' },
          },
        }}
      >
        <OperationsCell icon={<LocalShippingRoundedIcon />} color={colors.toShip} label={t('TO SHIP')} value={fmtInt(metrics.shipping.toShip)} tooltip={t('Orders waiting to be shipped during the selected dashboard period')} />
        <OperationsCell icon={<LocalShippingRoundedIcon />} color={colors.shipped} label={t('SHIPPED')} value={fmtInt(metrics.shipping.shipped)} tooltip={t('Orders shipped during the selected dashboard period')} />
        <OperationsCell icon={<ReceiptLongRoundedIcon />} color={colors.credits} label={t('CREDITS')} value={fmtInt(metrics.creditNotes.qty)} tooltip={t('Credit note count during the selected dashboard period')} />
        <OperationsCell icon={<ReceiptLongRoundedIcon />} color={colors.creditsValue} label={t('CREDITS VALUE')} value={fmtMoney(metrics.creditNotes.value)} tooltip={t('Total credit note net value during the selected dashboard period')} />
        <OperationsCell icon={<ShoppingCartRoundedIcon />} color={colors.purchasing} label={t('PURCHASING')} value={fmtMoney(metrics.purchasing.value)} tooltip={t('Total purchasing value during the selected dashboard period')} />
      </Box>
    </DashboardCard>
  );
};

/* ============================================================================
 * Whole dashboard (used by DashboardMain; also kept under the old name)
 * ==========================================================================*/

export const DashboardHistoricalChartCard: React.FC<{ chartPeriod: Period; selectedBars: DashboardBarKey[]; selectedSalesChannels: SalesChannel[]; viewBySalesRep?: boolean; selectedSalesReps?: string[]; salesRepsData?: SalesRep[]; dataProvider: ChartDataProvider; sizes?: CardSizeProps }> = (props) => (
  <DashboardChartCard period={props.chartPeriod} selectedBars={props.selectedBars} selectedSalesChannels={props.selectedSalesChannels} dataProvider={props.dataProvider} />
);

export const DashboardCards: React.FC<DashboardCardsProps> = (props) => {
  const metrics = useDashboardCardMetrics({
    dataProvider: props.dataProvider,
    period: props.chartPeriod,
    dashboardSales: props.dashboardSales,
    dashboardReturns: props.dashboardReturns,
    dashboardPurchasing: props.dashboardPurchasing,
    dashboardDispatched: props.dashboardDispatched,
    dashboardToShip: props.dashboardToShip,
    dashboardCreditNotes: props.dashboardCreditNotes,
    dashboardSalesMargins: props.dashboardSalesMargins,
  });

  return (
    <DashboardGrid>
      <DashboardChartCard period={props.chartPeriod} selectedBars={props.selectedBars} selectedSalesChannels={props.selectedSalesChannels} dataProvider={props.dataProvider} />
      <DashboardExecutiveSummaryCard metrics={metrics} period={props.chartPeriod} />
      <DashboardChannelBreakdownCard metrics={metrics} period={props.chartPeriod} />
      <DashboardOperationsOverviewCard metrics={metrics} period={props.chartPeriod} />
      {props.kpis ? <DashboardKpiOverviewCard kpis={props.kpis} /> : null}
    </DashboardGrid>
  );
};

/* ============================================================================
 * Legacy building blocks (unchanged API) — no longer used by the dashboard itself
 * ==========================================================================*/

export const DashboardHeadlineRow: React.FC<{
  title: string; tooltip: string; icon: React.ReactNode; iconColor: string; mainValue: string | number | React.ReactNode;
  currentValue: string | number; avg: string | number; currentLabel?: string; trendLabel?: string | null; trendColor?: string; extraFooter?: React.ReactNode;
}> = ({ title, tooltip, icon, iconColor, mainValue, currentValue, avg, currentLabel = 'Today', trendLabel, trendColor, extraFooter }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', py: 1, minWidth: 0 }}>
      <Tooltip title={tooltip} arrow placement="top">
        <Avatar sx={{ bgcolor: alpha(iconColor, 0.12), color: iconColor, mr: 2, width: 48, height: 48, flexShrink: 0 }}>{icon}</Avatar>
      </Tooltip>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{title}</Typography>
          {trendLabel ? <Typography variant="caption" sx={{ color: trendColor ?? 'text.secondary', fontWeight: 700, lineHeight: 1 }}>{trendLabel}</Typography> : null}
        </Box>
        <Box sx={{ mt: 0.25, mb: 0.5, minWidth: 0 }}>
          {typeof mainValue === 'string' || typeof mainValue === 'number'
            ? <Typography variant="h3" sx={{ fontWeight: 700, lineHeight: 1.1, color: 'text.primary', wordWrap: 'break-word' }}>{mainValue}</Typography>
            : mainValue}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">{currentLabel}:{' '}<Typography component="span" variant="caption" fontWeight={600} color="text.primary">{currentValue}</Typography></Typography>
          <Typography variant="caption" color="text.secondary">{t('Avg/day')}:{' '}<Typography component="span" variant="caption" fontWeight={600} color="text.primary">{avg}</Typography></Typography>
        </Box>
        {extraFooter ? <Box sx={{ mt: 1.1 }}>{extraFooter}</Box> : null}
      </Box>
    </Box>
  );
};

export const DashboardDualMetricRow: React.FC<{
  tooltip: string; icon: React.ReactNode; iconColor: string; leftTitle: string; leftValue: string | number; leftTooltip?: string;
  rightTitle: string; rightValue: string | number; rightTooltip?: string;
}> = ({ tooltip, icon, iconColor, leftTitle, leftValue, leftTooltip, rightTitle, rightValue, rightTooltip }) => {
  const block = (title: string, value: string | number, tip?: string) => {
    const content = (
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>{title}</Typography>
        <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.2, mt: 0.35 }}>{value}</Typography>
      </Box>
    );
    return tip ? <Tooltip title={tip} arrow placement="top">{content}</Tooltip> : content;
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', py: 1, minWidth: 0 }}>
      <Tooltip title={tooltip} arrow placement="top">
        <Avatar sx={{ bgcolor: alpha(iconColor, 0.12), color: iconColor, mr: 2, width: 48, height: 48, flexShrink: 0 }}>{icon}</Avatar>
      </Tooltip>
      <Box sx={{ flexGrow: 1, minWidth: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        {block(leftTitle, leftValue, leftTooltip)}
        {block(rightTitle, rightValue, rightTooltip)}
      </Box>
    </Box>
  );
};

export const DashboardChannelDonut: React.FC<{ online: number; store: number; onlineColor: string; storeColor: string; size?: number }> = ({ online, store, onlineColor, storeColor, size = 64 }) => {
  const total = online + store;
  const dash = (total === 0 ? 0 : online / total) * 100;
  return (
    <svg width={size} height={size} viewBox="0 0 42 42" style={{ transform: 'rotate(-90deg)', overflow: 'visible', flexShrink: 0 }}>
      <circle cx="21" cy="21" r={15.91549430918954} fill="transparent" stroke={total === 0 ? alpha('#808080', 0.2) : storeColor} strokeWidth="6" />
      {total > 0 && online > 0 && <circle cx="21" cy="21" r={15.91549430918954} fill="transparent" stroke={onlineColor} strokeWidth="6" strokeDasharray={`${dash} 100`} />}
    </svg>
  );
};
